import { useEffect } from 'react';

/**
 * SEO Component - Dynamic Meta Tag Management
 * Updates document head with page-specific SEO meta tags
 */
const SEO = ({ 
  title,
  description,
  keywords,
  ogImage = "https://customer-assets.emergentagent.com/job_pattaya-solicitors-1/artifacts/qbndhwu7_chantima-attorney.jpg",
  path = "/",
  schemaType = null // 'service', 'location', 'contact', 'about', 'criminal'
}) => {
  const siteUrl = "https://pattayasolicitors.com";
  const fullUrl = `${siteUrl}${path}`;

  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Helper to update or create meta tag
    const updateMetaTag = (attribute, attributeValue, content) => {
      let element = document.querySelector(`meta[${attribute}="${attributeValue}"]`);
      if (element) {
        element.setAttribute('content', content);
      } else {
        element = document.createElement('meta');
        element.setAttribute(attribute, attributeValue);
        element.setAttribute('content', content);
        document.head.appendChild(element);
      }
    };

    // Update meta tags
    if (description) {
      updateMetaTag('name', 'description', description);
    }
    if (keywords) {
      updateMetaTag('name', 'keywords', keywords);
    }

    // Open Graph tags
    updateMetaTag('property', 'og:title', title);
    updateMetaTag('property', 'og:description', description);
    updateMetaTag('property', 'og:url', fullUrl);
    updateMetaTag('property', 'og:image', ogImage);

    // Twitter tags
    updateMetaTag('name', 'twitter:title', title);
    updateMetaTag('name', 'twitter:description', description);
    updateMetaTag('name', 'twitter:image', ogImage);

    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', fullUrl);
    }

    // Remove existing page-specific schema
    const existingSchema = document.querySelector('script[data-page-schema="true"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add page-specific schema if provided
    if (schemaType) {
      const schema = getSchemaForType(schemaType, { title, description, fullUrl, ogImage });
      if (schema) {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-page-schema', 'true');
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      }
    }

    // Cleanup on unmount
    return () => {
      const pageSchema = document.querySelector('script[data-page-schema="true"]');
      if (pageSchema) {
        pageSchema.remove();
      }
    };
  }, [title, description, keywords, ogImage, path, fullUrl, schemaType]);

  return null; // This component doesn't render anything visible
};

// Generate schema based on page type
const getSchemaForType = (type, { title, description, fullUrl, ogImage }) => {
  const baseOrg = {
    "@type": "LegalService",
    "name": "Pattaya Solicitors",
    "legalName": "Pattaya Solicitors – DMC Inter Law & Consultants",
    "alternateName": [
      "Pattaya Lawyers Thailand",
      "Solicitors in Pattaya Thailand",
      "Law Firm Pattaya for Foreigners",
      "English Speaking Lawyer Pattaya",
      "Immigration Lawyer Pattaya Thailand"
    ],
    "url": "https://pattayasolicitors.com",
    "telephone": ["+6638412415", "+66818383869"],
    "email": "info@pattayasolicitors.com",
    "areaServed": "Pattaya, Chonburi, Thailand",
    "serviceType": [
      "Immigration Law",
      "Property Law",
      "Business Law",
      "Family Law",
      "Litigation & Civil Law"
    ],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "20/173, Moo 10, South Pattaya Road",
      "addressLocality": "Pattaya City",
      "addressRegion": "Chonburi",
      "postalCode": "20150",
      "addressCountry": "TH"
    }
  };

  switch (type) {
    case 'services':
      return {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "Legal Services",
        "name": "Pattaya Solicitors - Immigration Lawyer, Property Lawyer, Company Registration",
        "description": description,
        "url": fullUrl,
        "provider": baseOrg,
        "areaServed": "Pattaya, Chonburi, Thailand",
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Legal Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Visa & Immigration Services",
                "description": "Complete visa services for expats, tourists, and long-term residents in Thailand"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Work Permit Services",
                "description": "Professional assistance with work permit applications and renewals"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Thai Property Law",
                "description": "Navigate property investment and ownership with expert legal guidance"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Company Registration",
                "description": "Full business formation and company registration services in Thailand"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Family Law",
                "description": "Divorce, custody, and family legal matters support"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Will and Testament",
                "description": "Professionally drafted wills and estate planning"
              }
            }
          ]
        }
      };

    case 'criminal':
      return {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "Criminal Defence Legal Services",
        "name": "Criminal Law Defence - Pattaya Solicitors",
        "description": description,
        "url": fullUrl,
        "provider": baseOrg,
        "areaServed": {
          "@type": "City",
          "name": "Pattaya, Thailand"
        },
        "availableChannel": {
          "@type": "ServiceChannel",
          "serviceType": "Emergency Legal Services",
          "availableLanguage": ["English", "Thai"]
        }
      };

    case 'contact':
      return {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Contact Pattaya Solicitors",
        "description": description,
        "url": fullUrl,
        "mainEntity": {
          "@type": "LegalService",
          "name": "Pattaya Solicitors - DMC Inter Law & Consultants",
          "telephone": ["+6638412415", "+66818383869"],
          "email": "info@pattayasolicitors.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "20/173, Moo 10, South Pattaya Road",
            "addressLocality": "Pattaya City",
            "addressRegion": "Chonburi",
            "postalCode": "20150",
            "addressCountry": "TH"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+6638412415",
            "contactType": "customer service",
            "availableLanguage": ["English", "Thai"]
          }
        }
      };

    case 'location':
      return {
        "@context": "https://schema.org",
        "@type": "Place",
        "name": "Pattaya Solicitors Office - DMC Inter Law & Consultants",
        "description": description,
        "url": fullUrl,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "20/173, Moo 10, South Pattaya Road",
          "addressLocality": "Pattaya City",
          "addressRegion": "Chonburi",
          "postalCode": "20150",
          "addressCountry": "TH"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "12.923160",
          "longitude": "100.875907"
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "18:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": "Saturday",
            "opens": "09:00",
            "closes": "12:00"
          }
        ],
        "hasMap": "https://www.google.com/maps?cid=6994771648817136255"
      };

    case 'about':
      return {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": "About Pattaya Solicitors",
        "description": description,
        "url": fullUrl,
        "mainEntity": {
          "@type": "LegalService",
          "name": "Pattaya Solicitors - DMC Inter Law & Consultants",
          "alternateName": "DMC Inter Law",
          "description": "Professional legal services for expats and foreign residents in Pattaya, Thailand",
          "foundingDate": "2004",
          "knowsLanguage": ["en", "th"],
          "slogan": "Pattaya's Premier Legal Experts For Expats & Foreign Residents",
          "award": "Embassy Recommended Law Firm",
          "memberOf": {
            "@type": "Organization",
            "name": "Lawyer's Council of Thailand"
          }
        }
      };

    case 'whyus':
      return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Why Choose Pattaya Solicitors",
        "description": description,
        "url": fullUrl,
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": ["h1", "h2", ".hero-text"]
        },
        "mainEntity": {
          "@type": "LegalService",
          "name": "Pattaya Solicitors - DMC Inter Law & Consultants",
          "slogan": "20+ Years Serving Expats in Pattaya",
          "knowsAbout": [
            "Thai Immigration Law",
            "Property Law Thailand",
            "Criminal Defence",
            "Family Law",
            "Corporate Law Thailand"
          ]
        }
      };

    default:
      return null;
  }
};

export default SEO;
